/**
 * Client master form (FRD §3.1).
 *
 * The one-time setup that removes the repetitive part of the current process:
 * rooms are generated from a pattern here and never typed again. The live
 * preview under the builder is deliberate - an operator creating 200 rooms
 * needs to see what the pattern produced before committing to it.
 *
 * The pattern is sent to the SERVER, not the expanded list. The browser expands
 * it only to show that preview; the authority is backend/src/utils/b2b-rooms.js,
 * so a tampered or mistyped payload cannot define a client's inventory. Client
 * and rooms are created in one transaction - 200 rooms, or none.
 *
 * Layout follows the design source (B2BClientMaster → `ClientForm`): the four
 * sections plus the "Configuration check" aside, which exists so an operator
 * can see at a glance what is still missing before they save.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Check, ChevronDown, ChevronRight, CircleAlert, Trash2 } from 'lucide-react'
import { useColors } from '../../hooks/useColors'
import {
  Chip,
  Field,
  Modal,
  PillButton,
  SegmentedControl,
  SelectInput,
  TextArea,
  TextInput,
} from '../../features/b2b/components/primitives'
import { B2B_SERVICES, itemPriceKey } from '../../features/b2b/serviceCatalogue'
import { b2bErrorMessage } from '../../features/b2b/errorCopy'
import {
  emptyRange,
  generateRoomsFromRanges,
  parseCustomRoomList,
  summariseRooms,
} from '../../features/b2b/roomPattern'
import type { RoomRange } from '../../features/b2b/roomPattern'
import { b2bApi } from '../../services/b2bApi'
import type { B2bClientRecord, B2bRoomsInput } from '../../services/b2bApi'
import type { B2bClientType, BillingCycleLength } from '../../features/b2b/types'

interface ClientFormModalProps {
  client?: B2bClientRecord
  onClose: () => void
  onSaved: (clientId: string) => void
}

const TYPE_OPTIONS: Array<{ value: B2bClientType; label: string }> = [
  { value: 'pg', label: 'PG' },
  { value: 'hostel', label: 'Hostel' },
  { value: 'society', label: 'Society' },
  { value: 'corporate', label: 'Corporate' },
  { value: 'other', label: 'Other' },
]

const CYCLE_OPTIONS: Array<{ value: BillingCycleLength; label: string }> = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
]

/** One row of the design's "Configuration check" aside. */
function CheckRow({ label, done }: { label: string; done: boolean }) {
  const colors = useColors()

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <span
        aria-hidden
        style={{
          width: '20px',
          height: '20px',
          borderRadius: '999px',
          display: 'grid',
          placeItems: 'center',
          background: done ? 'rgba(21,128,61,0.12)' : colors.surface,
          color: 'var(--chip-success-ink)',
          flexShrink: 0,
        }}
      >
        {done ? <Check size={12} /> : null}
      </span>
      <span
        className="font-body"
        style={{ fontSize: '13px', color: done ? colors.textPrimary : colors.textMuted }}
      >
        {label}
      </span>
      <span className="sr-only">{done ? 'complete' : 'incomplete'}</span>
    </div>
  )
}

export default function ClientFormModal({ client, onClose, onSaved }: ClientFormModalProps) {
  const colors = useColors()
  const isEdit = Boolean(client)

  const [name, setName] = useState(client?.name ?? '')
  const [type, setType] = useState<B2bClientType>(client?.client_type ?? 'pg')
  const [address, setAddress] = useState(client?.address ?? '')
  const [pocName, setPocName] = useState(client?.poc_name ?? '')
  const [pocPhone, setPocPhone] = useState(client?.poc_phone ?? '')
  const [billingCycle, setBillingCycle] = useState<BillingCycleLength>(
    client?.billing_cycle_length ?? 'monthly',
  )

  const [roomMode, setRoomMode] = useState<'range' | 'custom'>('range')
  const [ranges, setRanges] = useState<RoomRange[]>([emptyRange('r1')])
  const [customList, setCustomList] = useState('')
  const [customBlock, setCustomBlock] = useState('')
  const [existingRoomCount, setExistingRoomCount] = useState(0)
  const [editingRooms, setEditingRooms] = useState(!isEdit)

  const [showRateCard, setShowRateCard] = useState(false)
  const [washIronPerKg, setWashIronPerKg] = useState('70')
  const [itemPrices, setItemPrices] = useState<Record<string, string>>({})
  const [rateCardTouched, setRateCardTouched] = useState(false)

  const [submitted, setSubmitted] = useState(false)
  const [saving, setSaving] = useState(false)
  const [serverError, setServerError] = useState('')

  /**
   * Editing loads the client's real rooms and negotiated rates. A create starts
   * from the catalogue defaults the server will seed anyway, so the two agree.
   */
  const loadExisting = useCallback(async () => {
    if (!client) return
    try {
      const [detail, rates] = await Promise.all([
        b2bApi.getClient(client.id),
        b2bApi.getRates(client.id),
      ])
      setExistingRoomCount(detail.room_count)
      setWashIronPerKg(String(rates.rate_card.washIronPerKg))
      setItemPrices(
        Object.entries(rates.rate_card.itemPrices).reduce<Record<string, string>>((acc, [key, value]) => {
          acc[key] = String(value)
          return acc
        }, {}),
      )
    } catch {
      // Non-fatal: the form still saves. Rooms and rates simply show as unknown
      // rather than blocking an operator who only wanted to fix a phone number.
      setExistingRoomCount(0)
    }
  }, [client])

  useEffect(() => {
    void loadExisting()
  }, [loadExisting])

  const pattern = useMemo(
    () =>
      roomMode === 'range'
        ? generateRoomsFromRanges(ranges)
        : parseCustomRoomList(customList, customBlock),
    [customBlock, customList, ranges, roomMode],
  )

  /** What the server will be asked to expand - never the expanded list itself. */
  const roomsInput = useMemo<B2bRoomsInput | undefined>(() => {
    if (!editingRooms) return undefined
    if (roomMode === 'custom') {
      const rooms = customList
        .split(/[,\n\r\t]+/)
        .map((token) => token.trim())
        .filter(Boolean)
      return rooms.length ? { mode: 'custom', rooms, block: customBlock.trim() || undefined } : undefined
    }
    const filled = ranges.filter((range) => String(range.from).trim() && String(range.to).trim())
    return filled.length
      ? {
          mode: 'ranges',
          ranges: filled.map((range) => ({
            block: range.block.trim() || undefined,
            floor: range.floor.trim() || undefined,
            from: range.from.trim(),
            to: range.to.trim(),
          })),
        }
      : undefined
  }, [customBlock, customList, editingRooms, ranges, roomMode])

  const plannedRoomCount = editingRooms ? pattern.rooms.length : existingRoomCount

  const phoneError =
    submitted && pocPhone.trim() && !/^\d{10}$/.test(pocPhone.trim())
      ? 'Enter a 10-digit phone number.'
      : undefined
  const nameError = submitted && !name.trim() ? 'Client name is required.' : undefined
  const addressError = submitted && !address.trim() ? 'Add the pickup point.' : undefined
  const roomsError =
    submitted && !isEdit && plannedRoomCount === 0 ? 'Add at least one room.' : undefined

  // The four checks the design's aside reports on.
  const checks = {
    profile: Boolean(name.trim() && address.trim()),
    rooms: plannedRoomCount > 0,
    billing: Boolean(billingCycle),
    contact: Boolean(pocName.trim() && /^\d{10}$/.test(pocPhone.trim())),
  }

  const canSave =
    Boolean(name.trim()) &&
    Boolean(address.trim()) &&
    (!pocPhone.trim() || /^\d{10}$/.test(pocPhone.trim())) &&
    (isEdit || plannedRoomCount > 0) &&
    !pattern.error &&
    pattern.duplicates.length === 0

  const buildRateCard = () => ({
    washIronPerKg: Number(washIronPerKg) || 0,
    itemPrices: Object.entries(itemPrices).reduce<Record<string, number>>((acc, [key, value]) => {
      const parsed = Number(value)
      if (Number.isFinite(parsed) && parsed > 0) acc[key] = parsed
      return acc
    }, {}),
  })

  const handleSave = async () => {
    setSubmitted(true)
    setServerError('')
    if (!canSave || saving) return

    const profile = {
      name: name.trim(),
      client_type: type,
      address: address.trim(),
      poc_name: pocName.trim() || null,
      poc_phone: pocPhone.trim() || null,
      billing_cycle_length: billingCycle,
    }

    setSaving(true)
    try {
      let clientId = client?.id

      if (client) {
        await b2bApi.updateClient(client.id, profile)
        if (roomsInput) await b2bApi.addRooms(client.id, roomsInput)
      } else {
        const result = await b2bApi.createClient({ ...profile, status: 'active', rooms: roomsInput })
        clientId = result.client.id
      }

      // Rates are their own endpoint, and only written when the operator
      // actually touched them - otherwise a create would immediately overwrite
      // the server's seeded defaults with the same values for no reason.
      if (clientId && rateCardTouched) {
        await b2bApi.replaceRates(clientId, buildRateCard())
      }

      if (clientId) onSaved(clientId)
    } catch (err) {
      setServerError(b2bErrorMessage(err, 'saving this client'))
    } finally {
      setSaving(false)
    }
  }

  const updateRange = (id: string, patch: Partial<RoomRange>) => {
    setRanges((current) => current.map((range) => (range.id === id ? { ...range, ...patch } : range)))
  }

  const currentRooms = { length: existingRoomCount }
  const previewChips = pattern.rooms.slice(0, 12)

  return (
    <Modal
      title={isEdit ? `Edit ${client?.name}` : 'Create B2B client'}
      onClose={onClose}
      width="920px"
      footer={
        <>
          <PillButton variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </PillButton>
          <PillButton onClick={() => void handleSave()} disabled={saving || (submitted && !canSave)}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Save client configuration'}
          </PillButton>
        </>
      }
    >
      {serverError ? (
        <div
          role="alert"
          style={{
            marginBottom: '14px',
            padding: '10px 12px',
            borderRadius: '10px',
            background: 'rgba(153,27,27,0.08)',
            border: '1px solid rgba(153,27,27,0.25)',
            color: 'var(--chip-danger-ink)',
            fontSize: '13px',
          }}
        >
          {serverError}
        </div>
      ) : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 260px',
          gap: '18px',
          alignItems: 'start',
        }}
      >
      <div style={{ display: 'grid', gap: '14px' }}>
        <Field label="Client / Housr Name" error={nameError}>
          <TextInput
            value={name}
            aria-label="Client name"
            placeholder="e.g. Green Valley PG"
            invalid={Boolean(nameError)}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <Field label="Client Type">
            <SelectInput
              value={type}
              aria-label="Client type"
              onChange={(event) => setType(event.target.value as B2bClientType)}
            >
              {TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectInput>
          </Field>
          <Field label="Billing Cycle" hint="Consolidated invoice per cycle.">
            <SegmentedControl
              size="sm"
              value={billingCycle}
              options={CYCLE_OPTIONS}
              onChange={setBillingCycle}
            />
          </Field>
        </div>

        <Field label="Address / Pickup Point" error={addressError}>
          <TextArea
            value={address}
            aria-label="Address"
            rows={2}
            invalid={Boolean(addressError)}
            placeholder="Building, locality, city"
            onChange={(event) => setAddress(event.target.value)}
          />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <Field label="POC Name">
            <TextInput
              value={pocName}
              aria-label="POC name"
              placeholder="Warden / manager"
              onChange={(event) => setPocName(event.target.value)}
            />
          </Field>
          <Field label="POC Phone" error={phoneError}>
            <TextInput
              value={pocPhone}
              aria-label="POC phone"
              inputMode="numeric"
              maxLength={10}
              invalid={Boolean(phoneError)}
              placeholder="10-digit number"
              onChange={(event) => setPocPhone(event.target.value.replace(/\D/g, ''))}
            />
          </Field>
        </div>

        {/* ------------------------------------------------------------ rooms */}
        <div
          style={{
            border: `1px solid ${colors.border}`,
            borderRadius: '12px',
            padding: '14px',
            background: colors.bgSecondary,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px',
              gap: '12px',
              flexWrap: 'wrap',
            }}
          >
            <div className="font-body" style={{ fontSize: '14px', fontWeight: 600 }}>
              Rooms
            </div>
            {editingRooms ? (
              <SegmentedControl
                size="sm"
                value={roomMode}
                options={[
                  { value: 'range', label: 'Range pattern' },
                  { value: 'custom', label: 'Custom list' },
                ]}
                onChange={setRoomMode}
              />
            ) : null}
          </div>

          {!editingRooms ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <Chip tone="neutral">{currentRooms.length} rooms already set up</Chip>
              <button
                type="button"
                className="font-body"
                onClick={() => setEditingRooms(true)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: colors.textPrimary,
                  fontWeight: 600,
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                Change room pattern
              </button>
            </div>
          ) : roomMode === 'range' ? (
            <div style={{ display: 'grid', gap: '10px' }}>
              {ranges.map((range, index) => (
                <div
                  key={range.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 1fr 32px',
                    gap: '8px',
                    alignItems: 'end',
                  }}
                >
                  <Field label={index === 0 ? 'Block' : undefined}>
                    <TextInput
                      value={range.block}
                      aria-label={`Block for range ${index + 1}`}
                      placeholder="A"
                      onChange={(event) => updateRange(range.id, { block: event.target.value })}
                      style={{ padding: '8px 10px' }}
                    />
                  </Field>
                  <Field label={index === 0 ? 'Floor' : undefined}>
                    <TextInput
                      value={range.floor}
                      aria-label={`Floor for range ${index + 1}`}
                      inputMode="numeric"
                      placeholder="1"
                      onChange={(event) =>
                        updateRange(range.id, { floor: event.target.value.replace(/\D/g, '') })
                      }
                      style={{ padding: '8px 10px' }}
                    />
                  </Field>
                  <Field label={index === 0 ? 'From' : undefined}>
                    <TextInput
                      value={range.from}
                      aria-label={`From room for range ${index + 1}`}
                      inputMode="numeric"
                      placeholder="1"
                      onChange={(event) =>
                        updateRange(range.id, { from: event.target.value.replace(/\D/g, '') })
                      }
                      style={{ padding: '8px 10px' }}
                    />
                  </Field>
                  <Field label={index === 0 ? 'To' : undefined}>
                    <TextInput
                      value={range.to}
                      aria-label={`To room for range ${index + 1}`}
                      inputMode="numeric"
                      placeholder="20"
                      onChange={(event) =>
                        updateRange(range.id, { to: event.target.value.replace(/\D/g, '') })
                      }
                      style={{ padding: '8px 10px' }}
                    />
                  </Field>
                  <button
                    type="button"
                    aria-label={`Remove range ${index + 1}`}
                    disabled={ranges.length === 1}
                    onClick={() =>
                      setRanges((current) => current.filter((entry) => entry.id !== range.id))
                    }
                    style={{
                      height: '38px',
                      borderRadius: '10px',
                      border: `1px solid ${colors.border}`,
                      background: colors.bgCard,
                      cursor: ranges.length === 1 ? 'not-allowed' : 'pointer',
                      opacity: ranges.length === 1 ? 0.4 : 1,
                    }}
                  >
                    <Trash2 size={14} color={colors.textMuted} />
                  </button>
                </div>
              ))}

              <button
                type="button"
                className="font-body"
                onClick={() =>
                  setRanges((current) => [...current, emptyRange(`r${current.length + 1}-${Date.now()}`)])
                }
                style={{
                  justifySelf: 'start',
                  border: 'none',
                  background: 'transparent',
                  color: colors.textPrimary,
                  fontWeight: 600,
                  fontSize: '13px',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                + Add another range
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '10px' }}>
              <Field label="Block (optional)">
                <TextInput
                  value={customBlock}
                  aria-label="Block for custom list"
                  placeholder="A"
                  onChange={(event) => setCustomBlock(event.target.value)}
                  style={{ padding: '8px 10px', maxWidth: '120px' }}
                />
              </Field>
              <Field label="Room numbers" hint="Separate with commas or new lines.">
                <TextArea
                  value={customList}
                  aria-label="Room numbers"
                  rows={3}
                  placeholder="101, 102, 103, G-1, G-2"
                  onChange={(event) => setCustomList(event.target.value)}
                />
              </Field>
            </div>
          )}

          {/* Live preview - what the pattern actually produced. */}
          {editingRooms ? (
            <div style={{ marginTop: '12px' }}>
              {pattern.error ? (
                <p className="font-body" style={{ margin: 0, fontSize: '12px', color: 'var(--color-error)' }}>
                  {pattern.error}
                </p>
              ) : pattern.duplicates.length > 0 ? (
                <p className="font-body" style={{ margin: 0, fontSize: '12px', color: 'var(--color-error)' }}>
                  Room {pattern.duplicates.slice(0, 3).join(', ')} appears twice.
                </p>
              ) : pattern.rooms.length === 0 ? (
                <p className="font-body" style={{ margin: 0, fontSize: '12px', color: colors.textMuted }}>
                  Fill a range to preview the rooms.
                </p>
              ) : (
                <>
                  <p
                    className="font-body"
                    style={{ margin: '0 0 8px', fontSize: '12px', color: colors.textMuted }}
                  >
                    Will create <strong style={{ color: colors.textPrimary }}>{pattern.rooms.length} rooms</strong>
                    {'. '}
                    {summariseRooms(pattern.rooms)}
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {previewChips.map((room) => (
                      <Chip key={`${room.block ?? ''}-${room.roomNumber}`}>{room.roomNumber}</Chip>
                    ))}
                    {pattern.rooms.length > previewChips.length ? (
                      <Chip tone="gold">+{pattern.rooms.length - previewChips.length} more</Chip>
                    ) : null}
                  </div>
                </>
              )}
              {roomsError ? (
                <p className="font-body" style={{ margin: '6px 0 0', fontSize: '12px', color: 'var(--color-error)' }}>
                  {roomsError}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* -------------------------------------------------------- rate card */}
        <div style={{ border: `1px solid ${colors.border}`, borderRadius: '12px' }}>
          <button
            type="button"
            className="font-body"
            onClick={() => setShowRateCard((current) => !current)}
            aria-expanded={showRateCard}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 14px',
              border: 'none',
              background: 'transparent',
              color: colors.textPrimary,
              fontWeight: 600,
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            {showRateCard ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            Default rates (optional)
          </button>

          {showRateCard ? (
            <div style={{ padding: '0 14px 14px', display: 'grid', gap: '12px' }}>
              <p className="font-body" style={{ margin: 0, fontSize: '12px', color: colors.textMuted }}>
                Reference only - the operator can override any price while billing.
              </p>

              <Field label="Wash & Iron (₹ per kg)">
                <TextInput
                  value={washIronPerKg}
                  aria-label="Wash and Iron rate per kg"
                  inputMode="decimal"
                  onChange={(event) => {
                    setRateCardTouched(true)
                    setWashIronPerKg(event.target.value)
                  }}
                  style={{ maxWidth: '140px' }}
                />
              </Field>

              {B2B_SERVICES.filter((service) => service.entryType === 'items').map((service) => (
                <div key={service.slug}>
                  <div
                    className="font-body"
                    style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      color: colors.textMuted,
                      marginBottom: '8px',
                    }}
                  >
                    {service.label}
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                      gap: '8px',
                    }}
                  >
                    {service.items.map((item) => {
                      const key = itemPriceKey(service.slug, item)
                      return (
                        <Field key={key} label={item}>
                          <TextInput
                            value={itemPrices[key] ?? ''}
                            aria-label={`${service.label} ${item} price`}
                            inputMode="decimal"
                            placeholder="₹"
                            onChange={(event) => {
                              setRateCardTouched(true)
                              setItemPrices((current) => ({ ...current, [key]: event.target.value }))
                            }}
                            style={{ padding: '8px 10px' }}
                          />
                        </Field>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>      </div>

      <aside
        style={{
          background: colors.bgSecondary,
          border: `1px solid ${colors.border}`,
          borderRadius: '12px',
          padding: '16px',
          display: 'grid',
          gap: '12px',
          position: 'sticky',
          top: 0,
        }}
      >
        <div
          className="font-body"
          style={{
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.6px',
            textTransform: 'uppercase',
            color: colors.textMuted,
          }}
        >
          Configuration check
        </div>

        <CheckRow label="Client profile" done={checks.profile} />
        <CheckRow label="Room structure" done={checks.rooms} />
        <CheckRow label="Billing defaults" done={checks.billing} />
        <CheckRow label="POC / Contact" done={checks.contact} />

        <div
          style={{
            borderTop: `1px solid ${colors.border}`,
            paddingTop: '12px',
            display: 'flex',
            gap: '8px',
            alignItems: 'flex-start',
          }}
        >
          <CircleAlert size={14} color={colors.accentText} style={{ flexShrink: 0, marginTop: '2px' }} />
          <p className="font-body" style={{ margin: 0, fontSize: '11px', color: colors.textMuted, lineHeight: 1.5 }}>
            Changing the room inventory later will not modify existing billing records.
          </p>
        </div>
      </aside>
      </div>
    </Modal>
  )
}
